import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { StatusBadge, MhdBadge } from "../components/Badges";
import { Package, Printer, Search, Warehouse, ArrowRightLeft, Clock, Move } from "lucide-react";
import { toast } from "sonner";

export default function ShelfView() {
  const [groups, setGroups] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dragging, setDragging] = useState(null); // {batch_id, charge_number, from}
  const [dropTarget, setDropTarget] = useState(null); // shelf.code
  const [searchParams] = useSearchParams();
  const highlight = (searchParams.get("location") || "").toLowerCase();

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/warehouse/by-location"),
      api.get("/warehouse/location-history?limit=20"),
    ]).then(([g, h]) => {
      setGroups(g.data);
      setHistory(h.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const printShelfLabel = (location) => {
    openPdf(`/warehouse/shelf-label.pdf?location=${encodeURIComponent(location)}`)
      .catch(() => toast.error("Regal-Etikett konnte nicht geöffnet werden"));
  };

  const doMove = async (batchId, chargeNumber, newLoc) => {
    try {
      await api.post(`/batches/${batchId}/move`, {
        new_location: newLoc,
        notes: null,
      });
      toast.success(`${chargeNumber} → ${newLoc || "kein Regal"}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler bei Umlagerung"); }
  };

  const moveBatch = async (batch) => {
    const newLoc = window.prompt(
      `Charge ${batch.charge_number} umlagern.\nAktuell: ${batch.batch_location || "kein Regal"}\nNeuer Regalplatz:`,
      batch.batch_location || ""
    );
    if (newLoc === null) return;
    doMove(batch.batch_id, batch.charge_number, newLoc);
  };

  const onDragStart = (e, batch) => {
    setDragging({
      batch_id: batch.batch_id,
      charge_number: batch.charge_number,
      from: batch.batch_location || "",
    });
    e.dataTransfer.effectAllowed = "move";
    // Ghost image (safari fallback: leave default)
    try { e.dataTransfer.setData("text/plain", batch.charge_number); } catch (_) {}
  };
  const onDragEnd = () => { setDragging(null); setDropTarget(null); };
  const onDragOver = (e, code) => { e.preventDefault(); if (code !== dropTarget) setDropTarget(code); };
  const onDrop = (e, code) => {
    e.preventDefault();
    if (!dragging) return;
    const target = code || "";
    if (target === dragging.from) { setDragging(null); setDropTarget(null); return; }
    doMove(dragging.batch_id, dragging.charge_number, target);
    setDragging(null); setDropTarget(null);
  };

  const filtered = groups.filter((g) => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    if ((g.location || "").toLowerCase().includes(s)) return true;
    return g.batches.some((b) =>
      (b.item_name || "").toLowerCase().includes(s) ||
      (b.item_sku || "").toLowerCase().includes(s) ||
      (b.charge_number || "").toLowerCase().includes(s));
  });

  const totalBatches = groups.reduce((n, g) => n + g.batches.length, 0);
  const totalLocations = groups.filter((g) => g.location).length;

  return (
    <div data-testid="page-shelf-view">
      <PageHeader
        eyebrow="Warehouse"
        title="Regal-Übersicht"
        description="Was liegt wo? Alle Chargen mit Bestand > 0. Tipp: Chargen per Drag &amp; Drop auf ein anderes Regal ziehen."
      />

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <Card className="p-4 border border-[var(--border)] rounded-2xl">
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]"><Warehouse size={14}/> REGALPLÄTZE</div>
          <div className="font-display text-2xl font-bold mt-1">{totalLocations}</div>
        </Card>
        <Card className="p-4 border border-[var(--border)] rounded-2xl">
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]"><Package size={14}/> AKTIVE CHARGEN</div>
          <div className="font-display text-2xl font-bold mt-1">{totalBatches}</div>
        </Card>
        <Card className="p-4 border border-[var(--border)] rounded-2xl">
          <div className="text-xs text-[var(--text-muted)]">FILTER</div>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input placeholder="Regal, Artikel, Charge…" value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-7 rounded-lg h-9" data-testid="input-shelf-search" />
          </div>
        </Card>
      </div>

      {loading && <div className="text-[var(--text-muted)]">Lade…</div>}

      {dragging && (
        <div className="fixed bottom-6 right-6 bg-neutral-900 text-white rounded-full px-4 py-2 text-sm shadow-lg z-50 flex items-center gap-2 pointer-events-none" data-testid="drag-hint">
          <Move size={14} /> Ziehe <b className="font-mono">{dragging.charge_number}</b> auf ein anderes Regal ({dragging.from || "aktuell keins"})
        </div>
      )}

      {history.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setHistoryOpen((v) => !v)}
            data-testid="btn-history-toggle"
            className="inline-flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text)] mb-2"
          >
            <Clock size={14} /> Umlagerungs-Protokoll {historyOpen ? "verbergen" : `(${history.length})`}
          </button>
          {historyOpen && (
            <div className="bg-white border border-[var(--border)] rounded-2xl p-4">
              <div className="space-y-1 text-xs font-mono max-h-64 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} data-testid={`history-row-${h.id}`}
                       className="py-1 border-b border-[var(--border)]/50 flex flex-wrap items-center gap-2">
                    <span className="text-[var(--text-muted)] w-36">{(h.created_at || "").slice(0, 19).replace("T", " ")}</span>
                    <span>{h.charge_number} · {h.item_name}</span>
                    <span className="text-[var(--text-muted)]">
                      <span className="font-mono">{h.from_location || "—"}</span>
                      <ArrowRightLeft size={10} className="inline mx-1" />
                      <span className="font-mono">{h.to_location || "—"}</span>
                    </span>
                    <span className="ml-auto text-[var(--text-muted)]">{h.user_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {filtered.map((g, idx) => {
          const loc = g.location || null;
          const isHighlighted = highlight && loc && loc.toLowerCase() === highlight;
          const isDropTarget = dragging && loc && dropTarget === loc && loc !== dragging.from;
          return (
            <div key={loc || `unassigned-${idx}`}
                 data-testid={`shelf-card-${loc || "unassigned"}`}
                 onDragOver={(e) => loc && onDragOver(e, loc)}
                 onDrop={(e) => loc && onDrop(e, loc)}
                 onDragLeave={() => setDropTarget(null)}
                 className={`bg-white border rounded-2xl overflow-hidden transition-all ${
                    isDropTarget ? "border-[var(--pop-yellow-dark)] ring-4 ring-[var(--pop-yellow)]/40 scale-[1.005]" :
                    isHighlighted ? "border-[var(--pop-pink)] ring-2 ring-[var(--pop-pink)]/30" :
                    "border-[var(--border)]"
                  }`}>
              <div className="p-4 flex items-center justify-between flex-wrap gap-3 bg-[var(--pop-cream)]/40 border-b border-[var(--border)]">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-mono font-bold ${loc ? "bg-[#fef3c7] text-[#854d0e]" : "bg-gray-100 text-gray-500"}`}>
                    {loc ? loc.split("-")[0] : "—"}
                  </div>
                  <div>
                    <div className="font-display text-xl font-bold">
                      {loc ? <span className="font-mono">{loc}</span> : <span className="text-[var(--text-muted)]">Kein Regal zugewiesen</span>}
                    </div>
                    <div className="text-xs text-[var(--text-muted)]">
                      {g.batches.length} Charge(n) · {g.total_units} Einheiten insgesamt
                      {isDropTarget && <span className="ml-2 font-bold text-[var(--pop-yellow-dark)]">← hier ablegen</span>}
                    </div>
                  </div>
                </div>
                {loc && (
                  <button onClick={() => printShelfLabel(loc)} data-testid={`btn-shelf-print-${loc}`}
                          className="inline-flex items-center gap-1 rounded-full bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] px-4 py-1.5 text-xs font-bold hover:bg-[var(--pop-yellow)] hover:text-white hover:border-transparent transition-colors">
                    <Printer size={14} /> Regal-Etikett (QR)
                  </button>
                )}
              </div>
              <div className="divide-y divide-[var(--border)]">
                {g.batches.map((b) => (
                  <div key={b.batch_id} data-testid={`shelf-batch-${b.charge_number}`}
                       draggable
                       onDragStart={(e) => onDragStart(e, b)}
                       onDragEnd={onDragEnd}
                       className={`flex items-center gap-3 p-3 text-sm cursor-grab active:cursor-grabbing hover:bg-[var(--pop-cream)]/20 ${dragging?.batch_id === b.batch_id ? "opacity-40" : ""}`}>
                    <Move size={12} className="text-[var(--text-muted)] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{b.item_name}</div>
                      <div className="text-xs text-[var(--text-muted)] font-mono">{b.item_sku} · {b.charge_number}</div>
                    </div>
                    <div className="text-xs font-mono w-24 text-right">
                      MHD: {b.mhd || "—"} <MhdBadge days={b.days_to_mhd} />
                    </div>
                    <div className="w-24 text-right"><StatusBadge status={b.quality_status} /></div>
                    <div className="w-24 text-right font-mono font-bold">
                      {b.quantity} <span className="text-xs text-[var(--text-muted)]">{b.item_unit}</span>
                    </div>
                    <button onClick={() => moveBatch(b)}
                            data-testid={`btn-move-${b.charge_number}`}
                            title="Umlagern (per Prompt)"
                            className="text-[var(--pop-pink)] hover:opacity-70 ml-2">
                      <ArrowRightLeft size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-[var(--border)] rounded-2xl p-10 text-center text-[var(--text-muted)]">
            Keine Regalplätze gefunden.
          </div>
        )}
      </div>
    </div>
  );
}
